import { Card, CardContent } from '@/components/ui/card'
import {
  Users,
  ClipboardList,
  CheckSquare,
  MessageSquare,
  FileText,
  FolderOpen,
  GitBranch,
  UserCheck,
} from 'lucide-react'
import type { User, Workspace, Project, Task, Message, Document } from '@/types'

// Define the node types that support relationship counting
export type NodeWithRelationships = User | Workspace | Project | Task | Message | Document

// Define the relationship types that can be counted
export type RelationshipType =
  | 'users'
  | 'workspaces'
  | 'projects'
  | 'tasks'
  | 'messages'
  | 'documents'
  | 'subtasks'
  | 'dependencies'
  | 'parentTask'
  | 'assignee'
  | 'creator'

// Configuration for each relationship type
const RELATIONSHIP_CONFIG = {
  users: { icon: Users, label: 'Users', color: 'text-blue-600' },
  workspaces: { icon: FolderOpen, label: 'Workspaces', color: 'text-purple-600' },
  projects: { icon: ClipboardList, label: 'Projects', color: 'text-green-600' },
  tasks: { icon: CheckSquare, label: 'Tasks', color: 'text-orange-600' },
  messages: { icon: MessageSquare, label: 'Messages', color: 'text-indigo-600' },
  documents: { icon: FileText, label: 'Documents', color: 'text-gray-600' },
  subtasks: { icon: GitBranch, label: 'Subtasks', color: 'text-teal-600' },
  dependencies: { icon: GitBranch, label: 'Dependencies', color: 'text-red-600' },
  parentTask: { icon: GitBranch, label: 'Parent Task', color: 'text-yellow-600' },
  assignee: { icon: UserCheck, label: 'Assignee', color: 'text-pink-600' },
  creator: { icon: UserCheck, label: 'Creator', color: 'text-cyan-600' },
} as const

// Define which relationships are available for each node type
const ENTITY_RELATIONSHIPS: Record<string, RelationshipType[]> = {
  User: ['workspaces', 'projects', 'tasks', 'messages', 'documents'],
  Workspace: ['users', 'projects', 'tasks', 'messages', 'documents'],
  Project: ['users', 'tasks', 'messages', 'documents'],
  Task: [
    'projects',
    'subtasks',
    'messages',
    'documents',
    'dependencies',
    'users',
    'assignee',
    'creator',
  ],
  Message: ['tasks', 'users', 'documents', 'projects', 'messages'],
  Document: ['users', 'tasks', 'projects', 'messages'],
}

export interface NodeRelationshipCountProps {
  /** The node to display relationship counts for */
  node: NodeWithRelationships
  /** Array of relationship types to display (defaults to all available for the node type) */
  relationships?: RelationshipType[]
  /** Custom className for the container */
  className?: string
  /** Whether to show the card with hover effects */
  interactive?: boolean
  /** Custom click handler */
  onClick?: () => void
}

// Component to render a single relationship count item
function RelationshipCountItem({
  type,
  count,
  showCount = true,
}: {
  type: RelationshipType
  count: number
  showCount?: boolean
}) {
  const config = RELATIONSHIP_CONFIG[type]
  const Icon = config.icon

  if (!showCount && count === 0) return null

  return (
    <div className="flex items-center gap-1">
      <Icon className={`h-4 w-4 ${config.color}`} />
      {showCount && <span className="text-muted-foreground text-sm">{count}</span>}
    </div>
  )
}

// Function to get relationship counts for an node
function getRelationshipCounts(node: NodeWithRelationships): Record<RelationshipType, number> {
  const counts: Record<RelationshipType, number> = {
    users: 0,
    workspaces: 0,
    projects: 0,
    tasks: 0,
    messages: 0,
    documents: 0,
    subtasks: 0,
    dependencies: 0,
    parentTask: 0,
    assignee: 0,
    creator: 0,
  }

  // Count relationships based on node type
  if ('users' in node) counts.users = node.users?.length || 0
  if ('workspaces' in node) counts.workspaces = node.workspaces?.length || 0
  if ('projects' in node) counts.projects = node.projects?.length || 0
  if ('tasks' in node) counts.tasks = node.tasks?.length || 0
  if ('messages' in node) counts.messages = node.messages?.length || 0
  if ('documents' in node) counts.documents = node.documents?.length || 0

  // Special handling for Task-specific relationships
  if ('subtasks' in node && node.subtasks) {
    counts.subtasks = node.subtasks.length
  }
  if ('dependencies' in node && node.dependencies) {
    counts.dependencies = node.dependencies.length
  }
  if ('parentTask' in node && node.parentTask) {
    counts.parentTask = 1
  }
  if ('assignee' in node && node.assignee) {
    counts.assignee = 1
  }
  if ('creator' in node && node.creator) {
    counts.creator = 1
  }

  return counts
}

export function NodeRelationshipCount({
  node,
  relationships,
  className = '',
  interactive = false,
  onClick,
}: NodeRelationshipCountProps) {
  // Get available relationships for this node type
  const availableRelationships = relationships || ENTITY_RELATIONSHIPS[node.constructor.name] || []

  // Get relationship counts
  const relationshipCounts = getRelationshipCounts(node)

  // Filter out relationships that don't apply to this node type
  const applicableRelationships = availableRelationships.filter(
    (rel) => relationshipCounts[rel] !== undefined
  )

  // Component for clickable/interactive card
  const CardComponent = interactive ? (
    <Card
      className={`cursor-pointer hover:shadow-lg transition-shadow ${className}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex flex-wrap gap-3">
          {applicableRelationships.map((relationship) => (
            <RelationshipCountItem
              key={relationship}
              type={relationship}
              count={relationshipCounts[relationship]}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  ) : (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {applicableRelationships.map((relationship) => (
        <RelationshipCountItem
          key={relationship}
          type={relationship}
          count={relationshipCounts[relationship]}
        />
      ))}
    </div>
  )

  return CardComponent
}
